import { useState, useEffect } from 'react';
import { apiFetch } from '../../../shared/lib/apiClient';

// Fields that are raw/operational — shown in the "Raw" column
const RAW_FIELDS = [
  'raw_description', 'raw_item_number', 'raw_unit_of_measure',
  'raw_unit_price', 'raw_quantity', 'raw_extended_amount',
];

// Fields that are internal DynamoDB keys — never shown
const SKIP_FIELDS = new Set([
  'company_id', 'document_id#line_item_id', 'document_id', 'line_item_id',
  'document_type', 'document_number', 'document_date',
  'vendor_id', 'vendor_name',
  'company_id#vendor_id', 'company_id#standardized_name_lower',
  'company_id#category', 'standardized_name_lower',
  'raw_description', 'raw_item_number', 'raw_unit_of_measure',
  'raw_unit_price', 'raw_quantity', 'raw_extended_amount', 'raw_extraction',
  'record_type', 'enriched_at', 'enrichment_version', 'enrichment_error',
  'enrichment_model', 'index', '_original',
]);

function formatValue(value) {
  if (value === null || value === undefined) return null;
  if (Array.isArray(value)) {
    return value.map((v, i) => (
      <span key={i} className="inline-block bg-zinc-100 text-zinc-600 rounded px-1.5 py-0.5 text-xs mr-1 mb-0.5">
        {typeof v === 'object' ? JSON.stringify(v) : String(v)}
      </span>
    ));
  }
  if (typeof value === 'object') return JSON.stringify(value);
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return value.toLocaleString();
  return String(value);
}

function EnrichedItemCard({ rawItem, enrichedItem, schema }) {
  const [expanded, setExpanded] = useState(false);

  // Build enriched fields list — everything not in SKIP_FIELDS
  const enrichedFields = enrichedItem
    ? Object.entries(enrichedItem).filter(([k]) => !SKIP_FIELDS.has(k))
    : [];

  // Split into core universal fields and domain-specific fields
  const UNIVERSAL_FIELDS = [
    'standardized_name', 'brand', 'category', 'subcategory', 'tags',
    'normalized_unit', 'normalized_unit_cost', 'conversion_notes',
    'comparison_unit', 'comparison_unit_cost',
    'is_new_from_vendor', 'price_vs_history',
  ];
  const coreFields = enrichedFields.filter(([k]) => UNIVERSAL_FIELDS.includes(k));
  const domainFields = enrichedFields.filter(([k]) => !UNIVERSAL_FIELDS.includes(k));

  const hasEnrichment = enrichedFields.length > 0;

  return (
    <div className="border border-zinc-200 rounded-lg overflow-hidden">
      {/* Row header — always visible */}
      <div
        className="flex items-start justify-between px-4 py-3 bg-white hover:bg-zinc-50/50 cursor-pointer transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {/* Standardized name (enriched) vs raw */}
            {enrichedItem?.standardized_name ? (
              <span className="text-sm font-medium text-zinc-900">{enrichedItem.standardized_name}</span>
            ) : (
              <span className="text-sm text-zinc-500 italic">{rawItem.raw_description || rawItem.description || '--'}</span>
            )}
            {enrichedItem?.brand && (
              <span className="text-xs text-zinc-400">· {enrichedItem.brand}</span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-1 flex-wrap">
            {enrichedItem?.category && (
              <span className="text-xs bg-violet-50 text-violet-600 px-2 py-0.5 rounded">{enrichedItem.category}</span>
            )}
            {enrichedItem?.subcategory && (
              <span className="text-xs text-zinc-400">{enrichedItem.subcategory}</span>
            )}
            {enrichedItem?.normalized_unit_cost != null && (
              <span className="text-xs text-emerald-600 font-medium">
                ${enrichedItem.normalized_unit_cost}/{enrichedItem.normalized_unit || 'unit'}
              </span>
            )}
            {enrichedItem?.comparison_unit_cost != null && enrichedItem.comparison_unit !== enrichedItem.normalized_unit && (
              <span className="text-xs text-blue-500 font-medium" title="Cross-vendor comparison unit">
                ${enrichedItem.comparison_unit_cost}/{enrichedItem.comparison_unit}
              </span>
            )}
            {rawItem.raw_unit_price != null && (
              <span className="text-xs text-zinc-400">
                Raw: ${rawItem.raw_unit_price} / {rawItem.raw_unit_of_measure || 'unit'}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 ml-3 shrink-0">
          {!hasEnrichment && (
            <span className="text-xs bg-amber-50 text-amber-600 px-2 py-0.5 rounded">No enrichment</span>
          )}
          {enrichedItem?.is_new_from_vendor === true && (
            <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded">New item</span>
          )}
          {enrichedItem?.is_new_from_vendor === null && (
            <span className="text-xs bg-zinc-100 text-zinc-400 px-2 py-0.5 rounded">First invoice</span>
          )}
          {enrichedItem?.price_vs_history && (
            <span className="text-xs bg-zinc-100 text-zinc-600 px-2 py-0.5 rounded">{enrichedItem.price_vs_history}</span>
          )}
          <svg
            className={`w-4 h-4 text-zinc-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Expanded detail — raw vs enriched */}
      {expanded && (
        <div className="border-t border-zinc-100 bg-zinc-50/30">
          <div className="grid grid-cols-2 divide-x divide-zinc-200">

            {/* Raw column */}
            <div className="p-4">
              <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-medium mb-3">Raw Extract</div>
              <dl className="space-y-2">
                {/* Top-level raw_* fields */}
                {Object.entries(rawItem)
                  .filter(([k, v]) => k.startsWith('raw_') && k !== 'raw_extraction' && v != null && v !== '')
                  .map(([k, v]) => (
                    <div key={k} className="flex gap-2">
                      <dt className="text-xs text-zinc-400 w-28 shrink-0">{k.replace(/^raw_/, '').replace(/_/g, ' ')}</dt>
                      <dd className="text-xs text-zinc-700 break-all">{String(v)}</dd>
                    </div>
                  ))}
                {/* All fields from raw_extraction (extra fields the extractor captured) */}
                {rawItem.raw_extraction && Object.entries(rawItem.raw_extraction)
                  .filter(([k, v]) => v != null && v !== '')
                  .map(([k, v]) => (
                    <div key={`rx_${k}`} className="flex gap-2">
                      <dt className="text-xs text-zinc-300 w-28 shrink-0 italic">{k.replace(/_/g, ' ')}</dt>
                      <dd className="text-xs text-zinc-500 break-all">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</dd>
                    </div>
                  ))}
              </dl>
            </div>

            {/* Enriched column */}
            <div className="p-4">
              <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-medium mb-3">
                Enriched
                {schema?.domain && (
                  <span className="ml-2 normal-case text-violet-500">· {schema.domain}</span>
                )}
              </div>
              {!hasEnrichment ? (
                <p className="text-xs text-zinc-400 italic">Enrichment not available for this item.</p>
              ) : (
                <div className="space-y-4">
                  {/* Universal fields */}
                  <dl className="space-y-2">
                    {coreFields.map(([key, value]) => (
                      <div key={key} className="flex gap-2">
                        <dt className="text-xs text-zinc-400 w-36 shrink-0">{key}</dt>
                        <dd className="text-xs text-zinc-800 break-all">{formatValue(value)}</dd>
                      </div>
                    ))}
                  </dl>

                  {/* Domain-specific fields */}
                  {domainFields.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-violet-400 font-medium mb-2">Domain Fields</div>
                      <dl className="space-y-2">
                        {domainFields.map(([key, value]) => (
                          <div key={key} className="flex gap-2">
                            <dt className="text-xs text-zinc-400 w-36 shrink-0">{key}</dt>
                            <dd className="text-xs text-zinc-800 break-all">{formatValue(value)}</dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  )}

                  {/* Conversion notes */}
                  {enrichedItem?.conversion_notes && (
                    <div className="bg-zinc-100 rounded px-3 py-2 text-xs text-zinc-500 italic">
                      {enrichedItem.conversion_notes}
                    </div>
                  )}

                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Inner detail view — shows enrichment for a single bill
const EnrichmentDetail = ({ invoiceId }) => {
  const [items, setItems] = useState(null);
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [enriching, setEnriching] = useState(false);

  const fetchEnrichment = () => {
    if (!invoiceId) return;
    setLoading(true);
    setError(null);

    apiFetch(`/api/bills/${invoiceId}/enriched`)
      .then((res) => {
        if (!res.ok) throw new Error(`API error ${res.status}`);
        return res.json();
      })
      .then((json) => {
        if (json.error) throw new Error(json.error);
        const all = json.items || [];
        const schemaRecord = all.find((i) => i.record_type === 'schema');
        const lineItems = all.filter((i) => i.record_type !== 'schema');
        setSchema(schemaRecord || null);
        setItems(lineItems);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchEnrichment(); }, [invoiceId]);

  const handleReenrich = async () => {
    setEnriching(true);
    try {
      const res = await apiFetch(`/api/bills/${invoiceId}/reenrich`, { method: 'POST' });
      if (!res.ok) throw new Error(`API error ${res.status}`);
      // Poll for enrichment results
      let attempts = 0;
      const maxAttempts = 30;
      const poll = setInterval(async () => {
        attempts++;
        try {
          const r = await apiFetch(`/api/bills/${invoiceId}/enriched`);
          const json = await r.json();
          const enrichedItems = (json.items || []).filter((i) => i.record_type !== 'schema');
          if (enrichedItems.length > 0 || attempts >= maxAttempts) {
            clearInterval(poll);
            setEnriching(false);
            fetchEnrichment();
          }
        } catch {
          if (attempts >= maxAttempts) {
            clearInterval(poll);
            setEnriching(false);
          }
        }
      }, 3000);
    } catch (err) {
      console.error('Re-enrich failed:', err);
      setEnriching(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-zinc-400 text-sm gap-2">
        <div className="w-4 h-4 border-2 border-zinc-300 border-t-zinc-600 rounded-full animate-spin" />
        Loading enrichment data...
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
        {error}
      </div>
    );
  }

  if (!items || items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        {enriching ? (
          <>
            <div className="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center mb-3">
              <svg className="w-5 h-5 text-violet-500 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" />
              </svg>
            </div>
            <p className="text-zinc-700 text-sm font-medium">Enriching line items...</p>
            <p className="text-zinc-400 text-xs mt-1">This typically takes 15–30 seconds. Results will appear automatically.</p>
            <div className="mt-4 flex items-center gap-2">
              <div className="flex gap-1">
                <div className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="w-10 h-10 rounded-xl bg-zinc-100 flex items-center justify-center mb-3">
              <svg className="w-5 h-5 text-zinc-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 9.776c.112-.017.227-.026.344-.026h15.812c.117 0 .232.009.344.026m-16.5 0a2.25 2.25 0 00-1.883 2.542l.857 6a2.25 2.25 0 002.227 1.932H19.05a2.25 2.25 0 002.227-1.932l.857-6a2.25 2.25 0 00-1.883-2.542m-16.5 0V6A2.25 2.25 0 016 3.75h3.879a1.5 1.5 0 011.06.44l2.122 2.12a1.5 1.5 0 001.06.44H18A2.25 2.25 0 0120.25 9v.776" />
              </svg>
            </div>
            <p className="text-zinc-500 text-sm font-medium">No enrichment data yet</p>
            <p className="text-zinc-400 text-xs mt-1">Enrichment runs automatically after extraction completes.</p>
            <button
              onClick={handleReenrich}
              className="mt-4 px-4 py-2 text-xs font-medium bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 transition-colors"
            >
              Run Enrichment
            </button>
          </>
        )}
      </div>
    );
  }

  const enrichedCount = items.filter((i) => i.standardized_name).length;

  return (
    <div className="space-y-4">
      {/* Re-enrich button / status */}
      <div className="flex justify-end">
        {enriching ? (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-violet-50 border border-violet-200 rounded-lg">
            <svg className="w-3.5 h-3.5 text-violet-500 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" />
            </svg>
            <span className="text-xs font-medium text-violet-600">Enriching... results will refresh automatically</span>
          </div>
        ) : (
          <button
            onClick={handleReenrich}
            className="px-3 py-1.5 text-xs font-medium bg-zinc-100 text-zinc-700 rounded-lg hover:bg-zinc-200 transition-colors flex items-center gap-1.5"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" />
            </svg>
            Re-enrich
          </button>
        )}
      </div>

      {/* Header stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white border border-zinc-200 rounded-lg px-4 py-3">
          <div className="text-xs text-zinc-400 mb-1">Line Items</div>
          <div className="text-lg font-semibold text-zinc-800">{items.length}</div>
        </div>
        <div className="bg-white border border-zinc-200 rounded-lg px-4 py-3">
          <div className="text-xs text-zinc-400 mb-1">Enriched</div>
          <div className="text-lg font-semibold text-emerald-600">{enrichedCount}</div>
        </div>
        <div className="bg-white border border-zinc-200 rounded-lg px-4 py-3">
          <div className="text-xs text-zinc-400 mb-1">Domain</div>
          <div className="text-sm font-medium text-violet-600 truncate">{schema?.domain || items[0]?.domain || '--'}</div>
        </div>
      </div>

      {/* Schema info */}
      {schema && (
        <div className="bg-zinc-50 border border-zinc-200 rounded-lg px-4 py-3">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-xs text-zinc-400 uppercase tracking-widest font-medium mb-1">Enrichment Schema</div>
              <div className="flex flex-wrap gap-1.5">
                {(schema.fields || []).map((f) => (
                  <span key={f.name} className="text-xs bg-white border border-zinc-200 text-zinc-600 px-2 py-0.5 rounded" title={f.description}>
                    {f.name}
                  </span>
                ))}
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-xs text-zinc-400">{schema.invoice_type}</div>
              <div className="text-xs text-zinc-500 mt-0.5">{(schema.fields || []).length} fields</div>
            </div>
          </div>
          {schema.schema_reasoning && (
            <p className="text-xs text-zinc-400 italic mt-2 border-t border-zinc-200 pt-2">{schema.schema_reasoning}</p>
          )}
        </div>
      )}

      {/* Line items — raw vs enriched */}
      <div className="space-y-2">
        {items.map((item) => (
          <EnrichedItemCard
            key={item.line_item_id || item['invoice_id#line_item_id']}
            rawItem={item}
            enrichedItem={item}
            schema={schema}
          />
        ))}
      </div>
    </div>
  );
};

// Standalone page — fetches bills list and lets user pick one
const EnrichmentComparisonView = ({ invoiceId: propInvoiceId, loaded }) => {
  // If invoiceId is passed as prop (embedded usage), just render detail directly
  if (propInvoiceId) {
    return <EnrichmentDetail invoiceId={propInvoiceId} />;
  }

  // Standalone page mode
  return <EnrichmentPage loaded={loaded} />;
};

const EnrichmentPage = ({ loaded }) => {
  const [bills, setBills] = useState([]);
  const [loadingBills, setLoadingBills] = useState(true);
  const [selectedBillId, setSelectedBillId] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    apiFetch('/api/bills')
      .then((r) => r.json())
      .then((data) => setBills(data.bills || []))
      .catch(() => {})
      .finally(() => setLoadingBills(false));
  }, []);

  const fmt = (n) => n != null ? `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '--';
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '--';

  const filtered = bills.filter((b) => {
    if (!search) return true;
    const s = search.toLowerCase();
    const vendor = (b.vendors?.display_name || b.vendor_name || '').toLowerCase();
    const num = (b.bill_number || '').toLowerCase();
    return vendor.includes(s) || num.includes(s);
  });

  const selectedBill = selectedBillId ? bills.find((b) => b.id === selectedBillId) : null;

  return (
    <div className={`space-y-6 transition-all duration-500 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
      {/* Header */}
      <div>
        <h1 className="text-2xl font-light text-zinc-900">Enrichment</h1>
        <p className="text-sm text-zinc-400 mt-1">View enriched line item data for your bills</p>
      </div>

      <div className="flex gap-6" style={{ minHeight: 400 }}>
        {/* Bill list */}
        <div className="w-80 shrink-0">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search bills..."
            className="w-full px-3 py-2 text-sm border border-zinc-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-zinc-300 mb-3"
          />

          {loadingBills ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-4 h-4 border-2 border-zinc-200 border-t-zinc-500 rounded-full animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-xs text-zinc-400 text-center py-8">No bills found</p>
          ) : (
            <div className="space-y-1 max-h-[calc(100vh-280px)] overflow-y-auto pr-1">
              {filtered.map((b) => {
                const vendor = b.vendors?.display_name || b.vendor_name || 'Unknown';
                const isSelected = b.id === selectedBillId;
                return (
                  <button
                    key={b.id}
                    onClick={() => setSelectedBillId(b.id)}
                    className={`w-full text-left px-3 py-2.5 rounded-lg border transition-all ${
                      isSelected
                        ? 'border-zinc-900 bg-zinc-900 text-white shadow-sm'
                        : 'border-zinc-100 bg-white hover:border-zinc-300 hover:shadow-sm'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className={`text-xs font-medium truncate ${isSelected ? 'text-white' : 'text-zinc-800'}`}>{vendor}</span>
                      <span className={`text-xs tabular-nums ${isSelected ? 'text-zinc-300' : 'text-zinc-500'}`}>{fmt(b.total_amount)}</span>
                    </div>
                    <div className="flex items-center justify-between mt-0.5">
                      <span className={`text-[10px] ${isSelected ? 'text-zinc-400' : 'text-zinc-400'}`}>
                        {b.bill_number || '--'} · {fmtDate(b.bill_date)}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Enrichment detail */}
        <div className="flex-1 min-w-0">
          {selectedBillId ? (
            <div>
              {selectedBill && (
                <div className="flex items-center gap-3 mb-4">
                  <h2 className="text-lg font-light text-zinc-900">
                    {selectedBill.vendors?.display_name || selectedBill.vendor_name || 'Unknown'}
                  </h2>
                  {selectedBill.bill_number && (
                    <span className="text-xs text-zinc-400">#{selectedBill.bill_number}</span>
                  )}
                </div>
              )}
              <EnrichmentDetail invoiceId={selectedBillId} />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <div className="w-12 h-12 rounded-xl bg-zinc-100 flex items-center justify-center mb-3">
                <svg className="w-6 h-6 text-zinc-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" />
                </svg>
              </div>
              <p className="text-zinc-500 text-sm font-medium">Select a bill</p>
              <p className="text-zinc-400 text-xs mt-1">Choose a bill from the list to view its enriched data</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EnrichmentComparisonView;
